import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  ...props
}: CalendarProps) {
  const def = getDefaultClassNames();
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn("p-3 bg-popover text-popover-foreground", className)}
      classNames={{
        root: cn("w-fit", def.root),
        months: cn("flex flex-col sm:flex-row gap-4", def.months),
        month: cn("flex flex-col gap-3", def.month),
        /* Nav + caption are stacked rows (natural flow) — no absolute
           positioning. Nav has prev on the left, next on the right with the
           caption visible alongside via order. */
        nav: cn(
          "flex items-center justify-between gap-2 px-1",
          def.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: "outline", size: "icon" }),
          "h-8 w-8 p-0 opacity-80 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline", size: "icon" }),
          "h-8 w-8 p-0 opacity-80 hover:opacity-100"
        ),
        month_caption: cn(
          "flex items-center justify-center min-h-9",
          def.month_caption
        ),
        dropdowns: cn(
          "flex items-center gap-2 text-sm font-medium",
          def.dropdowns
        ),
        dropdown_root: cn(
          "relative inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1.5 shadow-sm hover:bg-accent focus-within:ring-2 focus-within:ring-ring",
          def.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 cursor-pointer appearance-none opacity-0",
          def.dropdown
        ),
        caption_label: cn(
          "pointer-events-none select-none font-medium inline-flex items-center gap-1",
          def.caption_label
        ),
        weekdays: cn("flex", def.weekdays),
        weekday: cn("text-muted-foreground w-9 font-normal text-[0.8rem] select-none", def.weekday),
        week: cn("flex w-full mt-2", def.week),
        day: cn(
          "relative h-9 w-9 text-center text-sm p-0 select-none focus-within:relative focus-within:z-20",
          def.day
        ),
        day_button: cn(
          "h-9 w-9 rounded-md font-normal cursor-pointer hover:bg-accent hover:text-accent-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[selected-single=true]:hover:bg-primary",
          "disabled:opacity-50 disabled:pointer-events-none",
          def.day_button
        ),
        selected: cn("[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary", def.selected),
        today: cn("[&_button]:bg-accent [&_button]:text-accent-foreground", def.today),
        outside: cn("text-muted-foreground opacity-50", def.outside),
        disabled: cn("text-muted-foreground opacity-30", def.disabled),
        hidden: cn("invisible", def.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: c, ...rest }) => {
          if (orientation === "left") return <ChevronLeft className={cn("h-4 w-4", c)} {...rest} />;
          return <ChevronRight className={cn("h-4 w-4", c)} {...rest} />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
